/*
 * etikettenkaufen.com: Schnellcheck für Druckdaten im Browser (window.EkDruckcheck).
 * Liest Bildgröße, Transparenz und Farbraum (PNG, JPG, TIF), Seitengröße (PDF, AI, EPS) und SVG-Maße
 * und berechnet für freie Kontur die Schneidlinie mit 2 mm Abstand um das Motiv.
 * Die Datei verlässt dabei den Browser nicht.
 * Version 1.1, 13.10.2026: EXIF-Ausrichtung, SVG als UTF-8, Graustufen bei PNG und TIF,
 * DOS-EPS und alte AI-Dateien, Konturraster begrenzt (keine Hänger bei kleinen Maßen)
 */
(() => {
  'use strict';

  if (window.EkDruckcheck) return;

  const MASK_SIDE = 360; /* Kantenlänge der Maske für Transparenz und Kontur */
  const CONTOUR_SIDE = 220; /* Motiv für die Konturberechnung höchstens so viele Pixel */
  const PT = 25.4 / 72;
  const INF = 1e20;

  /** @param {string} name */
  const extOf = (name) => {
    const m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
  };

  /** @param {Blob} file @param {number} start @param {number} end */
  const bytesOf = async (file, start, end) => new Uint8Array(await file.slice(start, end).arrayBuffer());

  /** @param {Uint8Array} bytes */
  const latin1 = (bytes) => {
    let out = '';
    for (let i = 0; i < bytes.length; i += 8192) out += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
    return out;
  };

  /* ---------- Kopfdaten der Formate ---------- */

  /** JPEG: Breite, Höhe und Anzahl der Farbkanäle aus dem SOF-Abschnitt (4 = CMYK) */
  function jpegInfo(b) {
    if (b[0] !== 0xff || b[1] !== 0xd8) return null;
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i += 1; continue; }
      const m = b[i + 1];
      if (m === 0xff) { i += 1; continue; }
      if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
      const len = (b[i + 2] << 8) | b[i + 3];
      if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
        return { h: (b[i + 5] << 8) | b[i + 6], w: (b[i + 7] << 8) | b[i + 8], comps: b[i + 9] };
      }
      if (m === 0xda || len < 2) return null;
      i += 2 + len;
    }
    return null;
  }

  /** PNG: Breite und Höhe aus IHDR */
  function pngInfo(b) {
    if (b.length < 26 || b[0] !== 0x89 || b[1] !== 0x50 || b[2] !== 0x4e || b[3] !== 0x47) return null;
    const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
    return { w: dv.getUint32(16), h: dv.getUint32(20), colorType: b[25] };
  }

  /** TIFF: Breite, Höhe und Farbmodell aus dem ersten IFD (Photometric 5 = CMYK) */
  async function tiffInfo(file) {
    const head = await bytesOf(file, 0, 8);
    const le = head[0] === 0x49 && head[1] === 0x49;
    const be = head[0] === 0x4d && head[1] === 0x4d;
    if (!le && !be) return null;
    const hv = new DataView(head.buffer);
    if (hv.getUint16(2, le) !== 42) return null;
    const off = hv.getUint32(4, le);
    const ifd = await bytesOf(file, off, off + 2 + 12 * 64);
    if (ifd.length < 2) return null;
    const dv = new DataView(ifd.buffer);
    const n = Math.min(dv.getUint16(0, le), Math.floor((ifd.length - 2) / 12));
    const info = { w: 0, h: 0, photometric: -1, spp: 0 };
    for (let k = 0; k < n; k++) {
      const e = 2 + k * 12;
      const tag = dv.getUint16(e, le);
      const type = dv.getUint16(e + 2, le);
      const val = type === 3 ? dv.getUint16(e + 8, le) : dv.getUint32(e + 8, le);
      if (tag === 256) info.w = val;
      else if (tag === 257) info.h = val;
      else if (tag === 262) info.photometric = val;
      else if (tag === 277) info.spp = val;
    }
    return info.w && info.h ? info : null;
  }

  /** PDF und AI: MediaBox, TrimBox, BleedBox in mm und die Seitenzahl, soweit lesbar */
  function pdfInfo(txt) {
    const n = '(-?\\d+(?:\\.\\d+)?)';
    const box = (name) => {
      const m = txt.match(new RegExp('/' + name + '\\s*\\[\\s*' + n + '\\s+' + n + '\\s+' + n + '\\s+' + n + '\\s*\\]'));
      if (!m) return null;
      const w = Math.abs(parseFloat(m[3]) - parseFloat(m[1])) * PT;
      const h = Math.abs(parseFloat(m[4]) - parseFloat(m[2])) * PT;
      return w > 1 && h > 1 ? [w, h] : null;
    };
    const rot = txt.match(/\/Rotate\s+(-?\d+)/);
    const turned = rot && Math.abs(parseInt(rot[1], 10)) % 180 === 90;
    const swap = (b) => (b && turned ? [b[1], b[0]] : b);
    const count = txt.match(/\/Type\s*\/Pages\b[^>]*?\/Count\s+(\d+)/) || txt.match(/\/Count\s+(\d+)[^>]*?\/Type\s*\/Pages\b/);
    return { media: swap(box('MediaBox')), trim: swap(box('TrimBox')), bleed: swap(box('BleedBox')), pages: count ? parseInt(count[1], 10) : null };
  }

  /** EPS: Begrenzungsrahmen in mm */
  function epsInfo(txt) {
    const m = txt.match(/%%HiResBoundingBox:\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)/) ||
      txt.match(/%%BoundingBox:\s*(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)/);
    if (!m) return null;
    const w = Math.abs(parseFloat(m[3]) - parseFloat(m[1])) * PT;
    const h = Math.abs(parseFloat(m[4]) - parseFloat(m[2])) * PT;
    return w > 1 && h > 1 ? { media: [w, h] } : null;
  }

  /** SVG: Seitenverhältnis aus viewBox oder Breite/Höhe, Maß in mm, wenn mit Einheit angegeben */
  const UNIT_MM = { mm: 1, cm: 10, in: 25.4, pt: PT, pc: 12 * PT };
  function svgInfo(txt) {
    let doc;
    try { doc = new DOMParser().parseFromString(txt, 'image/svg+xml'); } catch (e) { return null; }
    const svg = doc && doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) return null;
    const vb = String(svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(parseFloat);
    const len = (v) => {
      const m = String(v || '').trim().match(/^(\d+(?:\.\d+)?)\s*([a-z%]*)$/i);
      return m ? { v: parseFloat(m[1]), u: m[2].toLowerCase() } : null;
    };
    const w = len(svg.getAttribute('width'));
    const h = len(svg.getAttribute('height'));
    let aw = 0;
    let ah = 0;
    if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) { aw = vb[2]; ah = vb[3]; }
    else if (w && h && w.u !== '%' && h.u !== '%') { aw = w.v; ah = h.v; }
    let mm = null;
    if (w && h && UNIT_MM[w.u] && UNIT_MM[h.u]) mm = [w.v * UNIT_MM[w.u], h.v * UNIT_MM[h.u]];
    const images = svg.getElementsByTagName('image').length;
    if (!aw || !ah) return { aspect: null, mm, doc: svg, images };
    return { aspect: aw / ah, mm, doc: svg, vw: aw, vh: ah, images };
  }

  /* ---------- Bild laden und Maske bilden ---------- */

  /** @param {string} url */
  const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild nicht lesbar'));
    img.src = url;
  });

  /**
   * Zeichnet das Bild klein auf eine Leinwand und liest den Alphakanal.
   * Ergebnis: Maske (1 = deckend), Anteil transparenter Pixel, Rahmen des deckenden Motivs.
   */
  function maskOf(img, natW, natH) {
    const W = natW || img.naturalWidth;
    const H = natH || img.naturalHeight;
    if (!W || !H) return null;
    const sc = Math.min(1, MASK_SIDE / Math.max(W, H));
    const mw = Math.max(1, Math.round(W * sc));
    const mh = Math.max(1, Math.round(H * sc));
    const cv = document.createElement('canvas');
    cv.width = mw;
    cv.height = mh;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, mw, mh);
    let data;
    try { data = ctx.getImageData(0, 0, mw, mh).data; } catch (e) { return null; }
    const mask = new Uint8Array(mw * mh);
    let clear = 0;
    let x0 = mw;
    let y0 = mh;
    let x1 = -1;
    let y1 = -1;
    for (let y = 0, i = 0; y < mh; y++) {
      for (let x = 0; x < mw; x++, i++) {
        const a = data[i * 4 + 3];
        if (a < 16) clear += 1;
        if (a >= 40) {
          mask[i] = 1;
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    const corner = (x, y) => data[(y * mw + x) * 4 + 3] < 16;
    const cornersClear = corner(0, 0) && corner(mw - 1, 0) && corner(0, mh - 1) && corner(mw - 1, mh - 1);
    const ratio = clear / (mw * mh);
    const transparent = ratio > 0.02 && x1 >= 0;
    const bbox = transparent ? { x0, y0, x1: x1 + 1, y1: y1 + 1 } : { x0: 0, y0: 0, x1: mw, y1: mh };
    return { mask, mw, mh, ratio, cornersClear, transparent, bbox, empty: x1 < 0 };
  }

  /* ---------- Analyse ---------- */

  /**
   * Liest eine Datei und gibt die Eckdaten zurück:
   * kind: raster | svg | pdf | eps | tif | other
   * px: [Breite, Höhe] in Pixeln (Raster), aspect: Seitenverhältnis des Motivs (ohne transparenten Rand),
   * motifPx: Pixelmaß des deckenden Motivs, mm: Seitenmaß in mm (PDF, AI, EPS, SVG mit Einheit),
   * cmyk: true | false | null, alpha: Maske für Transparenz und Kontur, url: Vorschau-Adresse (Raster, SVG)
   * @param {File} file
   */
  async function analyze(file) {
    const ext = extOf(file.name);
    const info = { name: file.name, ext, kind: 'other', px: null, aspect: null, motifPx: null, mm: null, trim: null, bleed: null,
      pages: null, cmyk: null, alpha: null, url: '', vector: false, problem: '' };
    try {
      if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
        info.kind = 'raster';
        const head = await bytesOf(file, 0, ext === 'png' ? 64 : 1048576);
        if (ext === 'png') {
          const p = pngInfo(head);
          if (p) {
            info.px = [p.w, p.h];
            info.gray = p.colorType === 0 || p.colorType === 4;
          }
          info.cmyk = false;
        } else {
          const j = jpegInfo(head);
          if (j) {
            info.px = [j.w, j.h];
            info.cmyk = j.comps === 4;
            info.gray = j.comps === 1;
          }
        }
        info.url = URL.createObjectURL(file);
        let img = null;
        try { img = await loadImage(info.url); } catch (e) { img = null; }
        if (img && img.naturalWidth && img.naturalHeight) {
          /* naturalWidth/-Height berücksichtigen die EXIF-Ausrichtung (Handyfotos), die Kopfdaten nicht */
          info.px = [img.naturalWidth, img.naturalHeight];
          info.alpha = maskOf(img, info.px[0], info.px[1]);
        } else if (!info.px) {
          info.problem = 'read';
        }
      } else if (ext === 'svg') {
        info.kind = 'svg';
        info.cmyk = false;
        const s = file.size > 0 && file.size <= 15728640 ? svgInfo(await file.text()) : null;
        if (!s) {
          info.problem = file.size > 15728640 ? 'big' : 'svg';
        } else {
          info.vector = true;
          info.mm = s.mm;
          info.embedded = s.images > 0;
          if (s.aspect) {
            /* Für die Maske feste Pixelmaße setzen, sonst zeichnen manche Browser SVGs ohne Größe nicht */
            const tw = s.aspect >= 1 ? MASK_SIDE : Math.round(MASK_SIDE * s.aspect);
            const th = s.aspect >= 1 ? Math.round(MASK_SIDE / s.aspect) : MASK_SIDE;
            s.doc.setAttribute('width', String(tw));
            s.doc.setAttribute('height', String(th));
            if (!s.doc.getAttribute('viewBox') && s.vw && s.vh) s.doc.setAttribute('viewBox', '0 0 ' + s.vw + ' ' + s.vh);
            const blob = new Blob([new XMLSerializer().serializeToString(s.doc)], { type: 'image/svg+xml' });
            info.url = URL.createObjectURL(blob);
            try {
              const img = await loadImage(info.url);
              info.alpha = maskOf(img, tw, th);
              info.px = [tw, th];
            } catch (e) {
              info.alpha = null;
            }
          }
          if (!info.url) info.url = URL.createObjectURL(file);
          if (!info.alpha && s.aspect) info.aspect = s.aspect;
        }
      } else if (ext === 'pdf' || ext === 'ai') {
        info.kind = 'pdf';
        const head = latin1(await bytesOf(file, 0, 2097152));
        let p = pdfInfo(head);
        if ((!p.media || !p.pages) && file.size > 2097152) {
          const tail = latin1(await bytesOf(file, Math.max(2097152, file.size - 1048576), file.size));
          const q = pdfInfo(tail);
          p = { media: p.media || q.media, trim: p.trim || q.trim, bleed: p.bleed || q.bleed, pages: p.pages || q.pages };
        }
        if (/%PDF-/.test(head.slice(0, 1024))) {
          info.vector = true;
          info.mm = p.media;
          info.trim = p.trim;
          info.bleed = p.bleed;
          info.pages = p.pages;
        } else if (ext === 'ai' && /^%!PS/.test(head)) {
          /* Illustrator bis Version 8: PostScript wie EPS */
          info.kind = 'eps';
          info.vector = true;
          const e = epsInfo(head.slice(0, 65536));
          if (e) info.mm = e.media;
        } else if (ext === 'pdf') {
          info.problem = 'pdf';
        } else {
          info.kind = 'other';
        }
      } else if (ext === 'eps') {
        info.kind = 'eps';
        let head = await bytesOf(file, 0, 65536);
        if (head[0] === 0xc5 && head[1] === 0xd0 && head[2] === 0xd3 && head[3] === 0xc6 && head.length >= 12) {
          /* DOS-EPS: PostScript-Teil steht an der angegebenen Stelle hinter einem Vorschaubild */
          const dv = new DataView(head.buffer, head.byteOffset, head.byteLength);
          const at = dv.getUint32(4, true);
          head = await bytesOf(file, at, at + 65536);
        }
        const txt = latin1(head);
        if (/^%!PS/.test(txt)) {
          info.vector = true;
          const e = epsInfo(txt);
          if (e) info.mm = e.media;
        } else {
          info.problem = 'eps';
        }
      } else if (ext === 'tif' || ext === 'tiff') {
        info.kind = 'tif';
        const t = await tiffInfo(file);
        if (t) {
          info.px = [t.w, t.h];
          info.cmyk = t.photometric === 5;
          info.gray = t.photometric === 0 || t.photometric === 1;
        }
      }
    } catch (err) {
      info.problem = info.problem || 'read';
    }

    /* Seitenverhältnis und Pixelmaß des eigentlichen Motivs (ohne transparenten Rand) */
    const a = info.alpha;
    if (a && !a.empty) {
      const bw = a.bbox.x1 - a.bbox.x0;
      const bh = a.bbox.y1 - a.bbox.y0;
      info.aspect = bw / bh;
      if (info.px) info.motifPx = [info.px[0] * bw / a.mw, info.px[1] * bh / a.mh];
    } else if (info.px && !info.aspect) {
      info.aspect = info.px[0] / info.px[1];
      info.motifPx = info.px.slice();
    }
    if (!info.aspect && info.trim) info.aspect = info.trim[0] / info.trim[1];
    else if (!info.aspect && info.mm) info.aspect = info.mm[0] / info.mm[1];
    if (info.kind === 'raster' && a && a.empty) info.problem = 'empty';
    return info;
  }

  /* ---------- Kontur ---------- */

  /** Exakte euklidische Distanz (Felzenszwalb und Huttenlocher), eindimensional */
  function edt1d(f, n, d, v, z) {
    let k = 0;
    v[0] = 0;
    z[0] = -INF;
    z[1] = INF;
    for (let q = 1; q < n; q++) {
      let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (s <= z[k]) {
        k -= 1;
        s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      }
      k += 1;
      v[k] = q;
      z[k] = s;
      z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1] < q) k += 1;
      d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
    }
  }

  /** Quadrierte Distanz jedes Pixels zum nächsten gesetzten Pixel */
  function edt(grid, w, h) {
    const out = new Float64Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = grid[i] ? 0 : INF;
    const n = Math.max(w, h);
    const f = new Float64Array(n);
    const d = new Float64Array(n);
    const v = new Int32Array(n);
    const z = new Float64Array(n + 1);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) f[y] = out[y * w + x];
      edt1d(f, h, d, v, z);
      for (let y = 0; y < h; y++) out[y * w + x] = d[y];
    }
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) f[x] = out[row + x];
      edt1d(f, w, d, v, z);
      for (let x = 0; x < w; x++) out[row + x] = d[x];
    }
    return out;
  }

  /** Löcher füllen: alles, was vom Rand aus nicht erreichbar ist, gehört zum Aufkleber */
  function fillHoles(g, w, h) {
    const seen = new Uint8Array(w * h);
    const stack = [];
    const push = (i) => { if (!g[i] && !seen[i]) { seen[i] = 1; stack.push(i); } };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (stack.length) {
      const i = stack.pop();
      const x = i % w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (i >= w) push(i - w);
      if (i < w * (h - 1)) push(i + w);
    }
    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) out[i] = seen[i] ? 0 : 1;
    return out;
  }

  /** Anzahl zusammenhängender Flächen (4er-Nachbarschaft), kleine Krümel zählen nicht */
  function countParts(g, w, h, minArea) {
    const seen = new Uint8Array(w * h);
    let parts = 0;
    const stack = [];
    for (let s = 0; s < w * h; s++) {
      if (!g[s] || seen[s]) continue;
      let area = 0;
      seen[s] = 1;
      stack.push(s);
      while (stack.length) {
        const i = stack.pop();
        area += 1;
        const x = i % w;
        const nb = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w];
        for (const j of nb) {
          if (j >= 0 && j < w * h && g[j] && !seen[j]) { seen[j] = 1; stack.push(j); }
        }
      }
      if (area >= minArea) parts += 1;
    }
    return parts;
  }

  /* Marching Squares: Segmente je Zelle (Ecken oben links, oben rechts, unten rechts, unten links) */
  const CASES = {
    1: [['l', 'b']], 2: [['b', 'r']], 3: [['l', 'r']], 4: [['t', 'r']], 5: [['t', 'r'], ['l', 'b']], 6: [['t', 'b']],
    7: [['l', 't']], 8: [['l', 't']], 9: [['t', 'b']], 10: [['l', 't'], ['b', 'r']], 11: [['t', 'r']], 12: [['l', 'r']],
    13: [['b', 'r']], 14: [['l', 'b']]
  };

  /** Umriss der Fläche als Ringe (Punkte in Pixelkoordinaten), der größte zuerst */
  function rings(g, w, h) {
    const val = (x, y) => (x >= 0 && y >= 0 && x < w && y < h ? g[y * w + x] : 0);
    const pt = (x, y, e) => {
      if (e === 't') return [x + 0.5, y];
      if (e === 'r') return [x + 1, y + 0.5];
      if (e === 'b') return [x + 0.5, y + 1];
      return [x, y + 0.5];
    };
    const segs = [];
    const ends = new Map();
    const key = (p) => p[0] * 2 * 100000 + p[1] * 2;
    const add = (p, idx) => {
      const k = key(p);
      const list = ends.get(k);
      if (list) list.push(idx); else ends.set(k, [idx]);
    };
    for (let y = -1; y < h; y++) {
      for (let x = -1; x < w; x++) {
        const c = (val(x, y) << 3) | (val(x + 1, y) << 2) | (val(x + 1, y + 1) << 1) | val(x, y + 1);
        const list = CASES[c];
        if (!list) continue;
        for (const [e1, e2] of list) {
          const a = pt(x, y, e1);
          const b = pt(x, y, e2);
          const idx = segs.length;
          segs.push([a, b]);
          add(a, idx);
          add(b, idx);
        }
      }
    }
    const used = new Uint8Array(segs.length);
    const out = [];
    for (let s = 0; s < segs.length; s++) {
      if (used[s]) continue;
      used[s] = 1;
      const ring = [segs[s][0]];
      let cur = segs[s][1];
      let guard = segs.length + 2;
      while (guard-- > 0) {
        ring.push(cur);
        const cand = ends.get(key(cur)) || [];
        const next = cand.find((i) => !used[i]);
        if (next === undefined) break;
        used[next] = 1;
        const sg = segs[next];
        cur = key(sg[0]) === key(cur) ? sg[1] : sg[0];
        if (key(cur) === key(ring[0])) break;
      }
      if (ring.length > 3) out.push(ring);
    }
    const area = (r) => {
      let s = 0;
      for (let i = 0; i < r.length; i++) {
        const p = r[i];
        const q = r[(i + 1) % r.length];
        s += p[0] * q[1] - q[0] * p[1];
      }
      return Math.abs(s / 2);
    };
    return out.sort((a, b) => area(b) - area(a));
  }

  /** Ramer-Douglas-Peucker für einen geschlossenen Ring */
  function simplify(points, eps) {
    if (points.length < 8) return points;
    const sq = eps * eps;
    const distSq = (p, a, b) => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const l = dx * dx + dy * dy;
      let t = l ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l : 0;
      t = Math.max(0, Math.min(1, t));
      const x = a[0] + t * dx - p[0];
      const y = a[1] + t * dy - p[1];
      return x * x + y * y;
    };
    const rdp = (pts) => {
      const keep = new Uint8Array(pts.length);
      keep[0] = 1;
      keep[pts.length - 1] = 1;
      const stack = [[0, pts.length - 1]];
      while (stack.length) {
        const [i, j] = stack.pop();
        let best = -1;
        let bd = sq;
        for (let k = i + 1; k < j; k++) {
          const d = distSq(pts[k], pts[i], pts[j]);
          if (d > bd) { bd = d; best = k; }
        }
        if (best > 0) { keep[best] = 1; stack.push([i, best], [best, j]); }
      }
      return pts.filter((_, k) => keep[k]);
    };
    /* Ring am weitesten entfernten Punkt teilen, beide Hälften vereinfachen */
    let far = 0;
    let fd = -1;
    for (let k = 1; k < points.length; k++) {
      const dx = points[k][0] - points[0][0];
      const dy = points[k][1] - points[0][1];
      if (dx * dx + dy * dy > fd) { fd = dx * dx + dy * dy; far = k; }
    }
    const a = rdp(points.slice(0, far + 1));
    const b = rdp(points.slice(far).concat([points[0]]));
    return a.slice(0, -1).concat(b.slice(0, -1));
  }

  /** Chaikin-Glättung für geschlossene Ringe */
  function smooth(points, rounds) {
    let pts = points;
    for (let r = 0; r < rounds; r++) {
      const out = [];
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const q = pts[(i + 1) % pts.length];
        out.push([0.75 * p[0] + 0.25 * q[0], 0.75 * p[1] + 0.25 * q[1]], [0.25 * p[0] + 0.75 * q[0], 0.25 * p[1] + 0.75 * q[1]]);
      }
      pts = out;
    }
    return pts;
  }

  /** Konvexe Hülle (Andrew) als Notlösung für weit auseinanderliegende Teile */
  function hull(g, w, h) {
    const pts = [];
    for (let y = 0; y < h; y++) {
      let first = -1;
      let last = -1;
      for (let x = 0; x < w; x++) if (g[y * w + x]) { if (first < 0) first = x; last = x; }
      if (first >= 0) { pts.push([first, y], [last + 1, y], [first, y + 1], [last + 1, y + 1]); }
    }
    pts.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (const p of pts) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i--) {
      const p = pts[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    return lower.slice(0, -1).concat(upper.slice(0, -1));
  }

  /**
   * Maske auf den Motivrahmen zuschneiden und verkleinern: höchstens CONTOUR_SIDE Zellen je Seite,
   * minF erzwingt gröbere Zellen (damit das Raster bei kleinen Maßen nicht riesig wird)
   */
  function cropMask(a, minF) {
    const bw = a.bbox.x1 - a.bbox.x0;
    const bh = a.bbox.y1 - a.bbox.y0;
    const f = Math.max(1, Math.max(bw, bh) / CONTOUR_SIDE, minF || 1);
    const cw = Math.max(1, Math.round(bw / f));
    const ch = Math.max(1, Math.round(bh / f));
    const out = new Uint8Array(cw * ch);
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        /* Zelle gilt als Motiv, wenn ein Pixel darin deckend ist */
        const sx0 = a.bbox.x0 + Math.floor(x * f);
        const sy0 = a.bbox.y0 + Math.floor(y * f);
        const sx1 = Math.min(a.bbox.x1, a.bbox.x0 + Math.max(Math.floor((x + 1) * f), Math.floor(x * f) + 1));
        const sy1 = Math.min(a.bbox.y1, a.bbox.y0 + Math.max(Math.floor((y + 1) * f), Math.floor(y * f) + 1));
        let on = 0;
        for (let yy = sy0; yy < sy1 && !on; yy++) for (let xx = sx0; xx < sx1; xx++) if (a.mask[yy * a.mw + xx]) { on = 1; break; }
        out[y * cw + x] = on;
      }
    }
    return { g: out, w: cw, h: ch, f };
  }

  const fmt = (v) => (Math.round(v * 100) / 100).toString();

  /**
   * Schneidkontur für freie Kontur.
   * W, H: Außenmaß des Aufklebers in mm, gap: Abstand der Kontur zum Motiv in mm (Standard 2).
   * Ergebnis in mm: path (SVG-Pfad), img (Lage des ganzen Bilds), motif (Lage des Motivs), parts, mode
   * mode: contour (folgt dem Motiv) | box (Rechteck um das Bild, wenn es keinen transparenten Hintergrund hat) | hull
   * @param {any} info Ergebnis von analyze()
   */
  function contour(info, W, H, gap = 2) {
    if (!info || !info.alpha || info.alpha.empty || !(W > gap * 2) || !(H > gap * 2)) return null;
    const a = info.alpha;
    const bw = a.bbox.x1 - a.bbox.x0;
    const bh = a.bbox.y1 - a.bbox.y0;
    /* Motiv so groß wie möglich in den Innenbereich (Außenmaß minus Abstand) */
    const s = Math.min((W - 2 * gap) / bw, (H - 2 * gap) / bh); /* mm je Maskenpixel */
    const mwmm = bw * s;
    const mhmm = bh * s;
    const ox = (W - mwmm) / 2;
    const oy = (H - mhmm) / 2;
    const img = { x: ox - a.bbox.x0 * s, y: oy - a.bbox.y0 * s, w: a.mw * s, h: a.mh * s };
    const motif = { x: ox, y: oy, w: mwmm, h: mhmm };

    if (!a.transparent) {
      const r = Math.min(gap, W / 4, H / 4);
      const x0 = ox - gap;
      const y0 = oy - gap;
      const x1 = ox + mwmm + gap;
      const y1 = oy + mhmm + gap;
      const path = 'M' + fmt(x0 + r) + ' ' + fmt(y0) + 'H' + fmt(x1 - r) + 'A' + fmt(r) + ' ' + fmt(r) + ' 0 0 1 ' + fmt(x1) + ' ' + fmt(y0 + r) +
        'V' + fmt(y1 - r) + 'A' + fmt(r) + ' ' + fmt(r) + ' 0 0 1 ' + fmt(x1 - r) + ' ' + fmt(y1) + 'H' + fmt(x0 + r) +
        'A' + fmt(r) + ' ' + fmt(r) + ' 0 0 1 ' + fmt(x0) + ' ' + fmt(y1 - r) + 'V' + fmt(y0 + r) + 'A' + fmt(r) + ' ' + fmt(r) + ' 0 0 1 ' + fmt(x0 + r) + ' ' + fmt(y0) + 'Z';
      return { path, img, motif, parts: 1, mode: 'box', bounds: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } };
    }

    /* Zelle mindestens 1/CONTOUR_SIDE der größeren Seite (mindestens 20 mm gerechnet): so bleibt das Raster
       samt Rand für Abstand und Schließen klein, auch bei kleinen Maßen während der Eingabe */
    const c = cropMask(a, Math.max(W, H, 20) / CONTOUR_SIDE / s);
    const sc = s * c.f; /* mm je Zelle der verkleinerten Maske */
    const r = gap / sc; /* Abstand in Zellen */
    const step = 1 / sc; /* 1 mm in Zellen */
    const close0 = 1 * step;
    const tries = 6;
    const pad = Math.ceil(r + close0 + tries * step) + 2;
    const gw = c.w + 2 * pad;
    const gh = c.h + 2 * pad;
    const grid = new Uint8Array(gw * gh);
    for (let y = 0; y < c.h; y++) for (let x = 0; x < c.w; x++) grid[(y + pad) * gw + x + pad] = c.g[y * c.w + x];
    const dist = edt(grid, gw, gh);
    const minArea = Math.max(4, (gw * gh) * 0.0015);
    let final = null;
    let mode = 'contour';
    for (let k = 0; k <= tries; k++) {
      const R = r + close0 + k * step;
      const dil = new Uint8Array(gw * gh);
      for (let i = 0; i < gw * gh; i++) dil[i] = dist[i] <= R * R ? 1 : 0;
      /* Schließen: um (R - r) wieder verkleinern, dann bleiben schmale Lücken zwischen Teilen überbrückt */
      const bg = new Uint8Array(gw * gh);
      for (let i = 0; i < gw * gh; i++) bg[i] = dil[i] ? 0 : 1;
      const dbg = edt(bg, gw, gh);
      const e = R - r;
      const closed = new Uint8Array(gw * gh);
      for (let i = 0; i < gw * gh; i++) closed[i] = dil[i] && dbg[i] > e * e ? 1 : 0;
      const filled = fillHoles(closed, gw, gh);
      if (countParts(filled, gw, gh, minArea) <= 1) { final = filled; break; }
    }
    let ring;
    if (final) {
      const all = rings(final, gw, gh);
      ring = all[0];
    }
    if (!ring) {
      const dil = new Uint8Array(gw * gh);
      for (let i = 0; i < gw * gh; i++) dil[i] = dist[i] <= r * r ? 1 : 0;
      ring = hull(dil, gw, gh);
      mode = 'hull';
    }
    if (!ring || ring.length < 3) return null;
    const pts = smooth(simplify(ring, 0.6), 2);
    let minx = INF;
    let miny = INF;
    let maxx = -INF;
    let maxy = -INF;
    const mm = pts.map((p) => {
      const x = ox + (p[0] - pad) * sc;
      const y = oy + (p[1] - pad) * sc;
      if (x < minx) minx = x;
      if (y < miny) miny = y;
      if (x > maxx) maxx = x;
      if (y > maxy) maxy = y;
      return fmt(x) + ' ' + fmt(y);
    });
    return { path: 'M' + mm.join('L') + 'Z', img, motif, parts: mode === 'hull' ? 2 : 1, mode, bounds: { x: minx, y: miny, w: maxx - minx, h: maxy - miny } };
  }

  /** Effektive Auflösung in dpi, wenn das Motiv mit mmW × mmH mm gedruckt wird */
  function dpi(info, mmW, mmH) {
    const px = info && (info.motifPx || info.px);
    if (!px || !(mmW > 0) || !(mmH > 0)) return null;
    return Math.min(px[0] / (mmW / 25.4), px[1] / (mmH / 25.4));
  }

  /** Druckmaß des Motivs, wenn es in einen Bereich innerW × innerH mm eingepasst wird */
  function fitBox(aspect, innerW, innerH) {
    if (!(aspect > 0)) return [innerW, innerH];
    return innerW / innerH > aspect ? [innerH * aspect, innerH] : [innerW, innerW / aspect];
  }

  /** Bekannte Seitenverhältnisse als Text, sonst als Dezimalzahl */
  function ratioText(a) {
    if (!(a > 0)) return '';
    const known = [[1, '1:1'], [4 / 3, '4:3'], [3 / 4, '3:4'], [3 / 2, '3:2'], [2 / 3, '2:3'], [16 / 9, '16:9'], [9 / 16, '9:16'],
      [2, '2:1'], [1 / 2, '1:2'], [5 / 4, '5:4'], [4 / 5, '4:5'], [Math.SQRT2, 'DIN quer'], [1 / Math.SQRT2, 'DIN hoch']];
    for (const [v, t] of known) if (Math.abs(a / v - 1) < 0.015) return t;
    return (a >= 1 ? (Math.round(a * 100) / 100) + ':1' : '1:' + (Math.round(100 / a) / 100)).replace(/\./g, ',');
  }

  window.EkDruckcheck = { analyze, contour, dpi, fitBox, ratioText, extOf, _test: { jpegInfo, pngInfo, pdfInfo, epsInfo, svgInfo, edt, rings, simplify } };
})();